import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { FiLink, FiX, FiCopy, FiEye, FiDownload, FiTrash2, FiTrendingUp, FiRefreshCw, FiSearch } from 'react-icons/fi';
import { showNotification } from './Notification';
import ConfirmDialog from './ConfirmDialog';
import ProgressBar from './ProgressBar';
import usePublicLinksBaseUrl from '../hooks/usePublicLinksBaseUrl';
import './Attendance.css';

const Attendance = () => {
  const linksBaseUrl = usePublicLinksBaseUrl();
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({
    course_title: '',
    attendance_date: '',
    has_sessions: false
  });
  const [selectedLink, setSelectedLink] = useState(null);
  const [records, setRecords] = useState([]);
  const [confirmDialog, setConfirmDialog] = useState({ isOpen: false, title: '', message: '', onConfirm: null });
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportFields, setExportFields] = useState([]);
  const [exportProgress, setExportProgress] = useState({ progress: 0, message: '' });
  const [generatingLink, setGeneratingLink] = useState(false);
  const [pollingInterval, setPollingInterval] = useState(null);
  const [deleteProgress, setDeleteProgress] = useState({ type: null, progress: 0, message: '' });
  const [showAnalyticsModal, setShowAnalyticsModal] = useState(false);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [traineeAnalytics, setTraineeAnalytics] = useState([]);
  const [analyticsSearch, setAnalyticsSearch] = useState('');
  const [batches, setBatches] = useState([]);
  const [selectedBatchId, setSelectedBatchId] = useState('');

  const stopPolling = () => {
    if (pollingInterval) {
      clearInterval(pollingInterval);
      setPollingInterval(null);
    }
  };

  useEffect(() => {
    fetchLinks();
    fetchBatches();
    
    // Cleanup polling on unmount
    return () => {
      stopPolling();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (showAnalyticsModal) {
      fetchTraineeAnalytics(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBatchId]);

  const fetchLinks = async () => {
    try {
      const response = await axios.get('/api/attendance/links');
      setLinks(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Error fetching links:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchBatches = async () => {
    try {
      const response = await axios.get('/api/batches');
      setBatches(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Error fetching batches:', error);
    }
  };

  const handleGenerateLink = async (e) => {
    e.preventDefault();
    try {
      setGeneratingLink(true);
      const response = await axios.post('/api/attendance/generate-link', {
        ...formData,
        batch_id: selectedBatchId || null
      });
      const fullUrl = `${linksBaseUrl}/attendance/${response.data.token}`;
      navigator.clipboard.writeText(fullUrl);
      showNotification(`Link generated and copied to clipboard!`, 'success');
      setShowModal(false);
      setFormData({ course_title: '', attendance_date: '', has_sessions: false });
      fetchLinks();
    } catch (error) {
      showNotification(error.response?.data?.error || 'Error generating link', 'error');
    } finally {
      setGeneratingLink(false);
    }
  };

  const handleViewRecords = async (linkId) => {
    try {
      const response = await axios.get(`/api/attendance/records/${linkId}`);
      setRecords(Array.isArray(response.data) ? response.data : []);
      setSelectedLink(linkId);
      
      // Start polling for new records
      startPolling(linkId);
    } catch (error) {
      showNotification('Error fetching records', 'error');
    }
  };

  const startPolling = (linkId) => {
    // Clear any existing polling
    if (pollingInterval) {
      clearInterval(pollingInterval);
    }

    // Poll every 3 seconds for new records
    const interval = setInterval(async () => {
      try {
        const response = await axios.get(`/api/attendance/records/${linkId}`);
        const newRecords = Array.isArray(response.data) ? response.data : [];
        
        // Get current state for comparison
        setRecords(currentRecords => {
          const safeCurrentRecords = Array.isArray(currentRecords) ? currentRecords : [];
          // Check for new records
          if (newRecords.length > safeCurrentRecords.length) {
            const newRecord = newRecords.find(r => !safeCurrentRecords.some(cr => cr.id === r.id));
            if (newRecord) {
              showNotification(
                `New attendance: ${newRecord.first_name} ${newRecord.last_name} (${newRecord.tag_number}) - ${newRecord.status}`,
                'info',
                5000
              );
            }
          }
          
          // Check for updated records (status changes)
          newRecords.forEach(newRecord => {
            const oldRecord = safeCurrentRecords.find(r => r.id === newRecord.id);
            if (oldRecord && oldRecord.status !== newRecord.status) {
              showNotification(
                `Attendance updated: ${newRecord.first_name} ${newRecord.last_name} (${newRecord.tag_number}) - ${oldRecord.status} → ${newRecord.status}`,
                'info',
                5000
              );
            }
          });
          
          return newRecords;
        });
      } catch (error) {
        console.error('Error polling records:', error);
      }
    }, 3000);

    setPollingInterval(interval);
  };

  const copyLink = (token) => {
    const fullUrl = `${linksBaseUrl}/attendance/${token}`;
    navigator.clipboard.writeText(fullUrl);
    showNotification('Link copied to clipboard!', 'success');
  };

  const deactivateLink = async (id) => {
    setConfirmDialog({
      isOpen: true,
      title: 'Deactivate Link',
      message: 'Are you sure you want to deactivate this attendance link?',
      onConfirm: async () => {
        try {
          await axios.put(`/api/attendance/link/${id}/deactivate`);
          showNotification('Link deactivated successfully', 'success');
          fetchLinks();
          setConfirmDialog({ isOpen: false, title: '', message: '', onConfirm: null });
        } catch (error) {
          showNotification('Error deactivating link', 'error');
          setConfirmDialog({ isOpen: false, title: '', message: '', onConfirm: null });
        }
      }
    });
  };

  const deleteLinkRecords = async (linkId) => {
    const link = Array.isArray(links) ? links.find(l => l.id === linkId) : null;
    setConfirmDialog({
      isOpen: true,
      title: 'Delete Attendance Records',
      message: `Are you sure you want to delete all attendance records for "${link?.course_title}"? This action cannot be undone.`,
      onConfirm: async () => {
        setDeleteProgress({ type: 'records', progress: 0, message: 'Deleting records...' });
        try {
          setDeleteProgress(prev => ({ ...prev, progress: 50 }));
          const response = await axios.delete(`/api/attendance/records/${linkId}`);
          setDeleteProgress(prev => ({ ...prev, progress: 100, message: 'Records deleted!' }));
          setTimeout(() => {
            setDeleteProgress({ type: null, progress: 0, message: '' });
            showNotification(response.data.message || 'Records deleted successfully', 'success');
            if (selectedLink === linkId) {
              setRecords([]);
              setSelectedLink(null);
              stopPolling();
            }
            fetchLinks();
            setConfirmDialog({ isOpen: false, title: '', message: '', onConfirm: null });
          }, 500);
        } catch (error) {
          setDeleteProgress({ type: null, progress: 0, message: '' });
          showNotification(error.response?.data?.error || 'Error deleting records', 'error');
          setConfirmDialog({ isOpen: false, title: '', message: '', onConfirm: null });
        }
      }
    });
  };

  const deleteLink = async (linkId) => {
    const link = Array.isArray(links) ? links.find(l => l.id === linkId) : null;
    setConfirmDialog({
      isOpen: true,
      title: 'Delete Attendance Link',
      message: `Are you sure you want to delete the link "${link?.course_title}" and all its attendance records? This action cannot be undone.`,
      onConfirm: async () => {
        setDeleteProgress({ type: 'link', progress: 0, message: 'Deleting link and records...' });
        try {
          setDeleteProgress(prev => ({ ...prev, progress: 50 }));
          await axios.delete(`/api/attendance/link/${linkId}`);
          setDeleteProgress(prev => ({ ...prev, progress: 100, message: 'Link deleted!' }));
          setTimeout(() => {
            setDeleteProgress({ type: null, progress: 0, message: '' });
            showNotification('Link and all records deleted successfully', 'success');
            if (selectedLink === linkId) {
              setRecords([]);
              setSelectedLink(null);
              stopPolling();
            }
            fetchLinks();
            setConfirmDialog({ isOpen: false, title: '', message: '', onConfirm: null });
          }, 500);
        } catch (error) {
          setDeleteProgress({ type: null, progress: 0, message: '' });
          showNotification(error.response?.data?.error || 'Error deleting link', 'error');
          setConfirmDialog({ isOpen: false, title: '', message: '', onConfirm: null });
        }
      }
    });
  };

  const deleteAllLinks = async () => {
    setConfirmDialog({
      isOpen: true,
      title: 'Delete All Attendance Links',
      message: 'Are you sure you want to delete ALL attendance links and ALL attendance records? This action cannot be undone!',
      onConfirm: async () => {
        setDeleteProgress({ type: 'all', progress: 0, message: 'Deleting all links and records...' });
        try {
          setDeleteProgress(prev => ({ ...prev, progress: 50 }));
          const response = await axios.delete('/api/attendance/links/all');
          setDeleteProgress(prev => ({ ...prev, progress: 100, message: 'All links deleted!' }));
          setTimeout(() => {
            setDeleteProgress({ type: null, progress: 0, message: '' });
            showNotification(response.data.message || 'All links and records deleted successfully', 'success');
            setRecords([]);
            setSelectedLink(null);
            stopPolling();
            fetchLinks();
            setConfirmDialog({ isOpen: false, title: '', message: '', onConfirm: null });
          }, 500);
        } catch (error) {
          setDeleteProgress({ type: null, progress: 0, message: '' });
          showNotification(error.response?.data?.error || 'Error deleting all links', 'error');
          setConfirmDialog({ isOpen: false, title: '', message: '', onConfirm: null });
        }
      }
    });
  };

  const attendanceExportFields = [
    { key: 'serial_number', label: 'SN' },
    { key: 'tag_number', label: 'Tag Number' },
    { key: 'first_name', label: 'First Name' },
    { key: 'last_name', label: 'Last Name' },
    { key: 'sex', label: 'Sex' },
    { key: 'phone_number', label: 'Phone Number' },
    { key: 'email', label: 'Email' },
    { key: 'course_title', label: 'Course Title' },
    { key: 'attendance_date', label: 'Attendance Date' },
    { key: 'session', label: 'Session' },
    { key: 'status', label: 'Attendance Status' },
    { key: 'recorded_at', label: 'Recorded At' }
  ];

  const handleExportAttendance = async (exportAll = false) => {
    if (!selectedLink) {
      showNotification('Please select an attendance link first', 'error');
      return;
    }

    setExportProgress({ progress: 0, message: 'Preparing export...' });
    
    try {
      setExportProgress(prev => ({ ...prev, progress: 20, message: 'Fetching attendance records...' }));
      const fields = exportAll ? 'all' : exportFields.join(',');
      
      setExportProgress(prev => ({ ...prev, progress: 40, message: 'Generating Excel file...' }));
      const response = await axios.get('/api/attendance/export/excel', {
        params: { linkId: selectedLink, fields },
        responseType: 'blob',
        onDownloadProgress: (progressEvent) => {
          if (progressEvent.total) {
            const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            setExportProgress(prev => ({ 
              ...prev, 
              progress: 40 + (percentCompleted * 0.5), 
              message: `Downloading... ${percentCompleted}%` 
            }));
          }
        }
      });

      setExportProgress(prev => ({ ...prev, progress: 90, message: 'Finalizing download...' }));
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `attendance_export_${Date.now()}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      setExportProgress(prev => ({ ...prev, progress: 100, message: 'Export completed successfully!' }));
      
      setTimeout(() => {
        setExportProgress({ progress: 0, message: '' });
        showNotification('Export completed successfully', 'success');
        setShowExportModal(false);
        setExportFields([]);
      }, 1000);
    } catch (error) {
      setExportProgress({ progress: 0, message: '' });
      showNotification('Error exporting data', 'error');
    }
  };

  const toggleExportField = (fieldKey) => {
    setExportFields(prev => 
      prev.includes(fieldKey) 
        ? prev.filter(f => f !== fieldKey)
        : [...prev, fieldKey]
    );
  };

  const fetchTraineeAnalytics = async (showLoader = true) => {
    try {
      if (showLoader) {
        setAnalyticsLoading(true);
      }
      const response = await axios.get('/api/attendance/analytics/trainees', {
        params: { batchId: selectedBatchId || '' }
      });
      setTraineeAnalytics(Array.isArray(response.data) ? response.data : []);
      if (!showAnalyticsModal) {
        setShowAnalyticsModal(true);
      }
    } catch (error) {
      showNotification('Error fetching trainee analytics', 'error');
    } finally {
      setAnalyticsLoading(false);
    }
  };

  const openAnalyticsModal = () => {
    setShowAnalyticsModal(true);
    fetchTraineeAnalytics(true);
  };

  const closeAnalyticsModal = () => {
    setShowAnalyticsModal(false);
    setAnalyticsSearch('');
  };

  const filteredAnalytics = Array.isArray(traineeAnalytics)
    ? traineeAnalytics.filter(item => {
        const tag = (item.tag_number || item.serial_number || '').toString().toLowerCase();
        const sn = (item.serial_number || '').toString().toLowerCase();
        const name = `${item.first_name || ''} ${item.last_name || ''}`.toLowerCase();
        const course = (item.course_title || '').toLowerCase();
        const searchTerm = analyticsSearch.trim().toLowerCase();

        if (!searchTerm) return true;

        return (
          tag.includes(searchTerm) ||
          sn.includes(searchTerm) ||
          name.includes(searchTerm) ||
          course.includes(searchTerm)
        );
      })
    : [];

  const formatPercentage = (value) => {
    if (value === null || value === undefined || Number.isNaN(Number(value))) {
      return '0%';
    }
    return `${Number(value).toFixed(1)}%`;
  };

  const activeBatch = Array.isArray(batches)
    ? batches.find(batch => String(batch.id) === String(selectedBatchId))
    : null;
  const activeBatchLabel = selectedBatchId ? (activeBatch?.name || 'Selected Batch') : 'All Batches';

  return (
    <div className="attendance">
      {(exportProgress.progress > 0 || deleteProgress.progress > 0) && (
        <div className="operation-progress-overlay">
          <div className="operation-progress-card">
            <ProgressBar 
              progress={deleteProgress.progress || exportProgress.progress} 
              message={deleteProgress.message || exportProgress.message}
            />
          </div>
        </div>
      )}
      
      <div className="attendance-header">
        <div className="attendance-header-left">
          <h1>Attendance Management</h1>
          <div className="batch-selector">
            <label htmlFor="attendance-batch">Batch Focus</label>
            <select
              id="attendance-batch"
              value={selectedBatchId}
              onChange={(e) => setSelectedBatchId(e.target.value)}
            >
              <option value="">All Batches</option>
              {Array.isArray(batches) && batches.map(batch => (
                <option key={batch.id} value={batch.id}>
                  {batch.name}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
          <button 
            className="btn btn-secondary analytics-button" 
            onClick={openAnalyticsModal}
            disabled={analyticsLoading}
          >
            <FiTrendingUp /> {analyticsLoading ? 'Loading Analytics...' : 'Trainee Analytics'}
          </button>
          {links.length > 0 && (
            <button 
              className="btn btn-danger" 
              onClick={deleteAllLinks}
              disabled={deleteProgress.type === 'all'}
            >
              <FiTrash2 /> Delete All Links
            </button>
          )}
          <button 
            className="btn btn-primary" 
            onClick={() => setShowModal(true)}
            disabled={generatingLink}
          >
            <FiLink /> {generatingLink ? 'Generating...' : 'Generate Attendance Link'}
          </button>
        </div>
      </div>

      {loading ? (
        <div className="loading">Loading...</div>
      ) : (
        <div className="links-list">
          {!Array.isArray(links) || links.length === 0 ? (
            <div className="empty-state">
              <p>No attendance links generated yet. Create one to get started.</p>
            </div>
          ) : (
            Array.isArray(links) && links.map(link => (
              <div key={link.id} className="link-card">
                <div className="link-header">
                  <div>
                    <h3>{link.course_title}</h3>
                    <p className="link-date">{new Date(link.attendance_date).toLocaleDateString()}</p>
                    <p className="link-batch">{link.batch_name || 'All Batches'}</p>
                    <span className={`session-badge ${link.has_sessions ? 'dual' : 'single'}`}>
                      {link.has_sessions ? 'Morning & Afternoon' : 'Single Session'}
                    </span>
                  </div>
                  <div className="link-status">
                    <span className={`status-badge ${link.is_active ? 'active' : 'inactive'}`}>
                      {link.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                </div>
                <div className="link-actions">
                  <button className="btn btn-secondary" onClick={() => copyLink(link.token)}>
                    <FiCopy /> Copy Link
                  </button>
                  <button className="btn btn-primary" onClick={() => handleViewRecords(link.id)}>
                    <FiEye /> View Records
                  </button>
                  {link.id === selectedLink && records.length > 0 && (
                    <>
                      <button className="btn btn-secondary" onClick={() => setShowExportModal(true)}>
                        <FiDownload /> Export
                      </button>
                      <button 
                        className="btn btn-danger" 
                        onClick={() => deleteLinkRecords(link.id)}
                        disabled={deleteProgress.type === 'records'}
                      >
                        <FiTrash2 /> Delete Records
                      </button>
                    </>
                  )}
                  <button 
                    className="btn btn-danger" 
                    onClick={() => deleteLink(link.id)}
                    disabled={deleteProgress.type === 'link'}
                  >
                    <FiTrash2 /> Delete Link
                  </button>
                  {link.is_active && (
                    <button className="btn btn-warning" onClick={() => deactivateLink(link.id)}>
                      Deactivate
                    </button>
                  )}
                </div>
                <div className="link-url">
                  <small>{linksBaseUrl}/attendance/{link.token}</small>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {showModal && (
        <div className="modal" onClick={() => setShowModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Generate Attendance Link</h2>
              <button className="close-btn" onClick={() => setShowModal(false)}>
                <FiX />
              </button>
            </div>
            <form onSubmit={handleGenerateLink}>
              {exportProgress.progress > 0 && exportProgress.progress < 100 && (
                <div style={{ marginBottom: '20px' }}>
                  <ProgressBar 
                    progress={exportProgress.progress} 
                    message={exportProgress.message}
                  />
                </div>
              )}
              <div className="form-group">
                <label>Course Title *</label>
                <input
                  type="text"
                  value={formData.course_title}
                  onChange={(e) => setFormData({ ...formData, course_title: e.target.value })}
                  required
                  placeholder="e.g., Introduction to Agriculture"
                  disabled={generatingLink}
                />
              </div>
              <div className="form-group">
                <label>Attendance Date *</label>
                <input
                  type="date"
                  value={formData.attendance_date}
                  onChange={(e) => setFormData({ ...formData, attendance_date: e.target.value })}
                  required
                  disabled={generatingLink}
                />
              </div>
              <div className="form-group checkbox-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={formData.has_sessions}
                    onChange={(e) => setFormData({ ...formData, has_sessions: e.target.checked })}
                    disabled={generatingLink}
                  />
                  <span>Enable Morning & Afternoon Sessions</span>
                </label>
                <small className="helper-text">
                  When enabled, coordinators can record attendance separately for both sessions.
                </small>
              </div>
              <div className="modal-actions">
                <button 
                  type="button" 
                  className="btn btn-secondary" 
                  onClick={() => { setShowModal(false); setExportProgress({ progress: 0, message: '' }); }}
                  disabled={generatingLink}
                >
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary" disabled={generatingLink}>
                  {generatingLink ? 'Generating...' : 'Generate Link'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {selectedLink && (
        <div className="modal" onClick={() => { stopPolling(); setSelectedLink(null); setRecords([]); }}>
          <div className="modal-content large" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                  <h2>Attendance Records</h2>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    {pollingInterval && (
                      <span style={{ 
                        fontSize: '12px', 
                        color: 'var(--success)', 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: '5px' 
                      }}>
                        <span style={{
                          width: '8px',
                          height: '8px',
                          borderRadius: '50%',
                          background: 'var(--success)',
                          animation: 'pulse 2s infinite'
                        }}></span>
                        Live Updates Active
                      </span>
                    )}
                    <button className="close-btn" onClick={() => { 
                      stopPolling();
                      setSelectedLink(null); 
                      setRecords([]);
                    }}>
                      <FiX />
                    </button>
                  </div>
                </div>
            <div className="records-table">
              <table className="table">
                <thead>
                  <tr>
                    <th>Tag Number</th>
                    <th>Name</th>
                    <th>Session</th>
                    <th>Status</th>
                    <th>Recorded At</th>
                  </tr>
                </thead>
                <tbody>
                  {!Array.isArray(records) || records.length === 0 ? (
                    <tr>
                      <td colSpan="4" style={{ textAlign: 'center' }}>No records yet</td>
                    </tr>
                  ) : (
                    records.map(record => (
                      <tr key={record.id}>
                        <td>{record.tag_number}</td>
                        <td>{record.first_name} {record.last_name}</td>
                        <td>{record.session === 'single' ? 'Single' : record.session ? record.session.charAt(0).toUpperCase() + record.session.slice(1) : '-'}</td>
                        <td>
                          <span className={`status-badge ${record.status}`}>
                            {record.status}
                          </span>
                        </td>
                        <td>{new Date(record.recorded_at).toLocaleString()}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {showAnalyticsModal && (
        <div className="modal" onClick={closeAnalyticsModal}>
          <div className="modal-content xlarge" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Trainee Attendance Analytics · {activeBatchLabel}</h2>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <button 
                  className="btn btn-secondary" 
                  onClick={() => fetchTraineeAnalytics(false)}
                  disabled={analyticsLoading}
                >
                  <FiRefreshCw /> {analyticsLoading ? 'Refreshing...' : 'Refresh'}
                </button>
                <button className="close-btn" onClick={closeAnalyticsModal}>
                  <FiX />
                </button>
              </div>
            </div>
            <div className="analytics-controls">
              <div className="analytics-search">
                <FiSearch className="analytics-search-icon" />
                <input
                  type="text"
                  placeholder="Quick search by tag number, name, or course..."
                  value={analyticsSearch}
                  onChange={(e) => setAnalyticsSearch(e.target.value)}
                />
              </div>
              <div className="analytics-meta">
                <span>Batch: {activeBatchLabel}</span>
                <span>Showing {filteredAnalytics.length} of {traineeAnalytics.length} rows</span>
              </div>
            </div>
            <div className="analytics-table-wrapper">
              {analyticsLoading ? (
                <div className="loading">Loading analytics...</div>
              ) : filteredAnalytics.length === 0 ? (
                <div className="empty-state">
                  <p>No analytics data found for the selected criteria.</p>
                </div>
              ) : (
                <table className="analytics-table">
                  <thead>
                    <tr>
                      <th>SN/Tag</th>
                      <th>Name</th>
                      <th>Batch</th>
                      <th>Course Title</th>
                      <th>Present</th>
                      <th>Absent</th>
                      <th>Total</th>
                      <th>Attendance %</th>
                      <th>Last Attendance</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredAnalytics.map((item, index) => (
                      <tr key={`${item.id}-${item.course_title}-${index}`}>
                        <td>
                          <div className="analytics-sn">
                            <span className="sn-tag">{item.tag_number || item.serial_number || 'N/A'}</span>
                            {item.serial_number && item.tag_number !== item.serial_number && (
                              <small>SN: {item.serial_number}</small>
                            )}
                          </div>
                        </td>
                        <td>
                          <div className="analytics-name">
                            <strong>{item.first_name} {item.last_name}</strong>
                            {item.specialization && (
                              <small>{item.specialization}</small>
                            )}
                          </div>
                        </td>
                        <td>{item.batch_name || 'Unassigned'}</td>
                        <td>{item.course_title || 'No Course Recorded'}</td>
                        <td>{item.present_count || 0}</td>
                        <td>{item.absent_count || 0}</td>
                        <td>{item.total_sessions || 0}</td>
                        <td>
                          <div className="analytics-percentage">
                            <span className={Number(item.attendance_percentage) >= 75 ? 'good' : Number(item.attendance_percentage) >= 50 ? 'average' : 'poor'}>
                              {formatPercentage(item.attendance_percentage)}
                            </span>
                          </div>
                        </td>
                        <td>
                          {item.last_attended_at 
                            ? new Date(item.last_attended_at).toLocaleString()
                            : 'No attendance yet'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      )}

      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        title={confirmDialog.title}
        message={confirmDialog.message}
        onConfirm={confirmDialog.onConfirm || (() => {})}
        onCancel={() => setConfirmDialog({ isOpen: false, title: '', message: '', onConfirm: null })}
      />

      {showExportModal && selectedLink && (
        <div className="modal" onClick={() => { setShowExportModal(false); setExportFields([]); }}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Export Attendance Records</h2>
              <button className="close-btn" onClick={() => { setShowExportModal(false); setExportFields([]); }}>
                <FiX />
              </button>
            </div>
            <div className="export-modal-content">
              <p>Select fields to export or export all fields:</p>
              {exportProgress.progress > 0 && (
                <div style={{ marginBottom: '20px' }}>
                  <ProgressBar 
                    progress={exportProgress.progress} 
                    message={exportProgress.message}
                  />
                </div>
              )}
              <div className="export-actions" style={{ marginBottom: '20px' }}>
                <button 
                  className="btn btn-primary" 
                  onClick={() => handleExportAttendance(true)}
                  disabled={exportProgress.progress > 0 && exportProgress.progress < 100}
                >
                  Export All Fields
                </button>
              </div>
              <div className="export-fields">
                <h3>Select Custom Fields:</h3>
                <div className="fields-grid">
                  {attendanceExportFields.map(field => (
                    <label key={field.key} className="field-checkbox">
                      <input
                        type="checkbox"
                        checked={exportFields.includes(field.key)}
                        onChange={() => toggleExportField(field.key)}
                      />
                      <span>{field.label}</span>
                    </label>
                  ))}
                </div>
              </div>
              <div className="modal-actions">
                <button type="button" className="btn btn-secondary" onClick={() => { setShowExportModal(false); setExportFields([]); }}>
                  Cancel
                </button>
                <button 
                  type="button" 
                  className="btn btn-primary" 
                  onClick={() => handleExportAttendance(false)}
                  disabled={exportFields.length === 0 || (exportProgress.progress > 0 && exportProgress.progress < 100)}
                >
                  {exportProgress.progress > 0 && exportProgress.progress < 100 ? 'Exporting...' : `Export Selected (${exportFields.length})`}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Attendance;

